
import { PARegistry } from "./pa-registry";
import type { CalendarWeek, CalendarWeekData, CalendarWeekDays } from "../../../_models/planning-assistant.interface";
import type { WeekDay } from "../classes/util";
import { PAUtil } from "../classes/util";
import type { HolidaysTypes } from "date-holidays";
import Holidays from "date-holidays";

export type HolidaysState = 'BB'|'BE'|'BW'|'BY'|'HB'|'HE'|'HH'|'MV'|'NI'|'NW'|'RP'|'SH'|'SL'|'SN'|'ST'|'TH'

export class PATimeControl {

  /** Anzahl initial angezeigter Kalenderwochen — lebt hier statt in CalendarComponent,
   *  damit kein Singleton eine Komponenten-Datei importieren muss */
  static defaultInitializedWeekAmount = 3;

  private static _instance: PATimeControl
  public static get Instance()
  {
    return this._instance || (this._instance = new this());
  }

  readonly holidayStates: HolidaysState[] = ['BB', 'BE', 'BW', 'BY', 'HB', 'HE', 'HH', 'MV', 'NI', 'NW', 'RP', 'SH', 'SL', 'SN', 'ST', 'TH']
  readonly holidayMap = new Map<string, Holidays>()

  readonly weekDays: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  readonly workDays: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']

  readonly germanStatesTranslations = {
    'de-bb': 'BB',
    'Brandenburg': 'BB',
    'de-be': 'BE',
    'Berlin': 'BE',
    'de-bw': 'BW',
    'Baden-Württemberg': 'BW',
    'de-by': 'BY',
    'Bayern': 'BY',
    'de-hb': 'HB',
    'Bremen': 'HB',
    'de-he': 'HE',
    'Hessen': 'HE',
    'de-hh': 'HH',
    'Hamburg': 'HH',
    'de-mv': 'MV',
    'Mecklenburg-Vorpommern': 'MV',
    'de-ni': 'NI',
    'Niedersachsen': 'NI',
    'de-nw': 'NW',
    'Nordrhein-Westfalen': 'NW',
    'de-rp': 'RP',
    'Rheinland-Pfalz': 'RP',
    'de-sh': 'SH',
    'Schleswig-Holstein': 'SH',
    'de-sl': 'SL',
    'Saarland': 'SL',
    'de-sn': 'SN',
    'Sachsen': 'SN',
    'de-st': 'ST',
    'Sachsen-Anhalt': 'ST',
    'de-th': 'TH',
    'Thüringen': 'TH',
  }

  public currentCalendarWeeksData: CalendarWeekData
  public selectedCalendarWeekData: CalendarWeekData
  public selectedCalendarDay: WeekDay = null
  public shownCalendarWeeksData: CalendarWeekData[]
  public currentDate = ''

  private constructor(
  ) {
    this.currentDate = this.calcCurrentDate()
    this.initHolidayMap()
  }

  private initHolidayMap(): void {
    this.holidayMap.set('DE', new Holidays('DE'))
    for (const state of this.holidayStates) {
      this.holidayMap.set(state, new Holidays('DE', state))
    }
  }

  public initCalendar(from_day_ts?: number) {
    this.calcCurrentCalendarWeeksData(from_day_ts)
    this.addDefaultCalendarWeeks()
  }

  private calcCurrentCalendarWeeksData(from_day_ts?: number): void {
    let current_day_timestamp = from_day_ts
    if (!current_day_timestamp) {
      const current_date = new Date(Date.parse(this.currentDate))
      current_day_timestamp = current_date.getTime()
    }
    const current_calendar_week = this.timestampToCalendarWeek(current_day_timestamp)

    this.currentCalendarWeeksData = this.calculateCalendarWeekData(current_calendar_week.number, current_calendar_week.year)
  }

  public addDefaultCalendarWeeks(): void {
    this.shownCalendarWeeksData = this.getCalendarWeekDataRange(this.currentCalendarWeeksData, PATimeControl.defaultInitializedWeekAmount - 1)
  }

  getCurrentCalendarWeekDayFilteredTimestamps(): number[] {
    const current_cwd = this.selectedCalendarWeekData || this.shownCalendarWeeksData[0]
    if (current_cwd) {
      return PARegistry.filterControl.filteredWeekDays.map(week_day => current_cwd.weekdays[week_day].timestamp)
    } else {
      return []
    }
  }

  incrementShownCalendarWeeks(): void {
    this.shiftShownCalendarWeeksByValue(1)
  }

  decrementShownCalendarWeeks(): void {
    this.shiftShownCalendarWeeksByValue(-1)
  }

  shiftShownCalendarWeeksByValue(value: number) {
    const shiftedCalendarWeeks = this.shownCalendarWeeksData.map( cwd => this.getCalendarWeekInNWeeks(value, cwd.weekdays.monday.timestamp))
    this.shownCalendarWeeksData = shiftedCalendarWeeks.map(cw => this.calculateCalendarWeekData(cw.number, cw.year))
  }

  incrementSelectedCalendarWeek(): void {
    this.shiftSelectedCalendarWeekByValue(1)
  }

  decrementSelectedCalendarWeek(): void {
    this.shiftSelectedCalendarWeekByValue(-1)
  }

  shiftSelectedCalendarWeekByValue(value: number) {
    this.shiftShownCalendarWeeksByValue(value)
    const shiftedCalendarWeek = this.getCalendarWeekInNWeeks(value, this.selectedCalendarWeekData.weekdays.monday.timestamp)
    this.selectedCalendarWeekData = this.calculateCalendarWeekData(shiftedCalendarWeek.number, shiftedCalendarWeek.year)
  }

  getCalendarWeekData(calendar_week: CalendarWeek): CalendarWeekData {
    const possible_calendar_week_data = this.shownCalendarWeeksData.find(calendar_week_data => this.equalCalendarWeeks(calendar_week_data.calendar_week, calendar_week))
    if (!possible_calendar_week_data) {
      const loaded_calendar_weeks_data = this.shownCalendarWeeksData.concat([this.calculateCalendarWeekData(calendar_week.number, calendar_week.year)])
      loaded_calendar_weeks_data.sort((cw_1, cw_2) => (
        cw_1.calendar_week.year < cw_2.calendar_week.year || (cw_1.calendar_week.year == cw_2.calendar_week.year && cw_1.calendar_week.number < cw_2.calendar_week.number)
      ) ? -1 : 1)
      return loaded_calendar_weeks_data.find(calendar_week_data => this.equalCalendarWeeks(calendar_week_data.calendar_week, calendar_week))
    } else {
      return possible_calendar_week_data
    }
  }

  equalCalendarWeeks(calendar_week_1: CalendarWeek, calendar_week_2: CalendarWeek) {
    return calendar_week_1.number == calendar_week_2.number && calendar_week_1.year == calendar_week_2.year;
  }

  getTimeStampsCalendarWeekData(timestamp: number): CalendarWeekData {
    return this.getCalendarWeekData(this.timestampToCalendarWeek(timestamp))
  }

  getTimeStampsWeekDay(timestamp: number): WeekDay {
    const date = new Date(timestamp)
    const day_idx = date.getDay()
    if (day_idx == 0) {
      return this.weekDays[6]
    }
    return this.weekDays[day_idx - 1]

  }

  getCurrentCalendarWeek(): CalendarWeek {
    const current_timestamp = Date.parse(this.calcCurrentDate() + 'T00:00:00.000Z')
    return this.timestampToCalendarWeek(current_timestamp)
  }

  getCalendarWeekInNWeeks(n: number, timestamp_base?: number): CalendarWeek {
    return this.getCalendarWeekInNDays(7 * n, timestamp_base)
  }

  getCalendarWeekInNDays(n: number, timestamp_base?: number): CalendarWeek {
    const date = timestamp_base ? new Date(timestamp_base) : new Date(this.calcCurrentDate() + 'T00:00:00.000Z')
    const timestamp_in_n_weeks = date.getTime() + (n * 1000 * 60 * 60 * 24)
    return this.timestampToCalendarWeek(timestamp_in_n_weeks)
  }

  calcCurrentDate(): string {
    const current_timestamp = this.getCurrentTimestamp()
    const date = new Date(current_timestamp)
    date.setHours(0, 0, 0, 0);
    return this.dateToDatestring(date, true, false)
  }

  calculateCalendarWeekData(calendar_week_number: number, calendar_week_year: number): CalendarWeekData {
    const nth_monday_timestamp = this.calendarWeekToTimestamp(calendar_week_year, calendar_week_number)

    const weekdays: CalendarWeekDays = this.initCalendarWeekDays()

    this.fillCalendarWeekDays(nth_monday_timestamp, weekdays);

    return {
      calendar_week: {
        number: calendar_week_number,
        year: calendar_week_year
      },
      weekdays: weekdays
    }
  }

  private fillCalendarWeekDays(nth_monday_timestamp: number, weekdays: CalendarWeekDays) {
    for (const weekday of this.weekDays) {
      const day_offset_to_monday = this.weekDays.indexOf(weekday)
      const ts = nth_monday_timestamp + (day_offset_to_monday * 24 * 3600 * 1000)
      weekdays[weekday].timestamp = ts

      const public_holidays = this.getTimestampsHolidays(ts).filter(hd => hd.type == 'public')
      if (public_holidays.length > 0) {
        weekdays[weekday].holidays.push(
          {
            holiday: public_holidays[0],
            tooltip_text: this.getPublicHolidayTooltipText(public_holidays[0]),
            public_regions: ['DE']
          }
        )
      } else {
        const regional_holidays: HolidaysTypes.Holiday[] = []
        const public_holiday_state_map = new Map<string, HolidaysState[]>()
        const partial_holiday_state_map = new Map<string, HolidaysState[]>()

        for (const state of this.holidayStates) {

          const state_holidays = this.getTimestampsHolidays(ts, state)

          for (const holiday of state_holidays) {

            if (!regional_holidays.find(hd => hd.name == holiday.name)) {
              regional_holidays.push(holiday)
            }

            if (holiday.type == 'public') {
              if (!public_holiday_state_map.has(holiday.name)) {
                public_holiday_state_map.set(holiday.name, [state])
              } else {
                public_holiday_state_map.get(holiday.name).push(state)
              }
            } else if (holiday.type == 'observance') {
              if (!partial_holiday_state_map.has(holiday.name)) {
                partial_holiday_state_map.set(holiday.name, [state])
              } else {
                partial_holiday_state_map.get(holiday.name).push(state)
              }
            }

          }
        }

        if (regional_holidays.find(rhd => rhd.type == 'public')) {
          weekdays[weekday].holidays = weekdays[weekday].holidays.concat(
            regional_holidays.map(
              rhd => {
                const public_regions = public_holiday_state_map.has(rhd.name) ? public_holiday_state_map.get(rhd.name) : []
                const partial_regions = partial_holiday_state_map.has(rhd.name) ? partial_holiday_state_map.get(rhd.name) : undefined
                return {
                  holiday: rhd,
                  tooltip_text: this.getRegionalHolidayTooltipText(rhd, public_regions, partial_regions),
                  public_regions: public_regions,
                  partial_regions: partial_regions
                }
              }
            )
          )
        }
      }
    }
  }

  private initCalendarWeekDays() {
    return {
      'monday': {
        timestamp: 0,
        holidays: []
      },
      'tuesday': {
        timestamp: 0,
        holidays: []
      },
      'wednesday': {
        timestamp: 0,
        holidays: []
      },
      'thursday': {
        timestamp: 0,
        holidays: []
      },
      'friday': {
        timestamp: 0,
        holidays: []
      },
      'saturday': {
        timestamp: 0,
        holidays: []
      },
      'sunday': {
        timestamp: 0,
        holidays: []
      }
    };
  }

  getPublicHolidayTooltipText(holiday: HolidaysTypes.Holiday): string {
    let res = holiday.name
    res += ' <br> '
    res += 'Gesetzlicher Feiertag'
    return res
  }

  getRegionalHolidayTooltipText(holiday: HolidaysTypes.Holiday, public_regions: HolidaysState[], partial_regions?: HolidaysState[]): string {
    let res = `<h6>${holiday.name}</h6><div class="body">`
    if (public_regions.length) {
      res += 'Gesetzl. Feiertag für Bundesländer: '
      res += public_regions.join(', ')
    }
    if (partial_regions?.length) {
      res += ' <br> '
      res += 'Teilw. Feiertag für: '
      res += partial_regions.join(', ')
    }
    return res + '</div>'
  }

  calendarWeekToTimestamp(calendar_week_year: number, calendar_week_number: number): number {
    const first_january = new Date(Date.UTC(calendar_week_year, 0, 1))
    const first_january_day = (first_january.getDay() - 1) % 7
    let day_offset_to_first_monday = -first_january_day
    if (first_january_day > 3) {
      day_offset_to_first_monday += 7
    }
    const first_monday_time_stamp = first_january.getTime() + day_offset_to_first_monday * 24 * 3600 * 1000

    return first_monday_time_stamp + (calendar_week_number - 1) * 7 * 24 * 3600 * 1000
  }

  getCalendarWeekDataRange(calendar_week_data: CalendarWeekData, amount_of_weeks_to_add: number): CalendarWeekData[] {

    const result = [calendar_week_data]

    for (const week_offset of PAUtil.range(1, amount_of_weeks_to_add)) {

      const weekdays: CalendarWeekDays = this.initCalendarWeekDays()
      this.fillCalendarWeekDays(calendar_week_data.weekdays.monday.timestamp + (week_offset * 7 * 24 * 3600000), weekdays)
      const calendar_week = this.timestampToCalendarWeek(weekdays['monday'].timestamp)

      result.push({
        calendar_week: {
          number: calendar_week.number,
          year: calendar_week.year
        },
        weekdays: weekdays
      })
    }

    return result
  }

  isHolidayInState(state: string, date_string: string): HolidaysTypes.Holiday[] | false {
    return this.holidayMap.get(state).isHoliday(date_string) || this.holidayMap.get('DE').isHoliday(date_string);
  }

  dateToDatestring(date: Date, reversed: boolean, utc_time: boolean, connector?: string): string {
    let day: string
    let month: string
    let year: string

    if (typeof connector == 'undefined') {
      connector = '-'
    }

    if (utc_time) {
      day = date.getUTCDate().toString()
      month = (date.getUTCMonth() + 1).toString()
      year = date.getUTCFullYear().toString()
    } else {
      day = date.getDate().toString()
      month = (date.getMonth() + 1).toString()
      year = date.getFullYear().toString()
    }

    if (day.length == 1) {
      day = "0" + day
    }
    if (month.length == 1) {
      month = "0" + month
    }
    let date_string: string
    if (reversed) {
      date_string = year + connector + month + connector + day
    } else {
      date_string = day + connector + month + connector + year
    }
    return date_string
  }

  timestampToDatestring(timestamp: number, reversed: boolean, connector?: string): string {
    return this.dateToDatestring(new Date(timestamp), reversed, false, connector)
  }

  timestampToUTCDatestring(timestamp: number, reversed: boolean, connector?: string): string {
    return this.dateToDatestring(new Date(timestamp), reversed, true, connector)
  }

  /**
   * Takes a `date` and returns a time string.
   *
   * @param date - The input Date
   * @param get_seconds - Boolean, it returns the seconds additionally to the hours and minutes
   *
   * @returns "Hour:Minute" if get_seconds is False, "Hour:Minute:Second" if get_seconds is True
   *
   */
  dateToTimestring(date: Date, get_seconds: boolean): string {
    let hours = date.getHours().toString()
    let minutes = date.getMinutes().toString()
    let secs: string
    if (hours.length == 1) {
      hours = "0" + hours
    }
    if (minutes.length == 1) {
      minutes = "0" + minutes
    }
    let res = hours + ":" + minutes
    if (get_seconds) {
      secs = date.getSeconds().toString()
      if (secs.length == 1) {
        secs = "0" + secs
        res = res + ":" + secs
      }
    }
    return res
  }

  dateToDateTimeString(date: Date, utc_time: boolean): string {
    return this.dateToDatestring(date, false, utc_time, '.') + ' ' + this.dateToTimestring(date, false) + 'Uhr'
  }

  minutesToTimeString(minutes: number) {
    let hour_string = Math.floor(minutes / 60).toString()
    if (hour_string.length == 1) {
      hour_string = '0' + hour_string
    }
    let minute_string = (minutes % 60).toString()
    if (minute_string.length == 1) {
      minute_string = '0' + minute_string
    }
    return hour_string + ':' + minute_string
  }

  /**
   * returns the Calendar-Week to a specific `time_stamp`
   */
  timestampToCalendarWeek(time_stamp: number): CalendarWeek {
    const date = new Date()
    date.setTime(time_stamp)
    const first_january = new Date(Date.UTC(date.getFullYear(), 0, 1))

    const first_january_day = (first_january.getDay() - 1) % 7

    let day_offset_to_first_monday = -first_january_day
    if (first_january_day > 3) {
      day_offset_to_first_monday += 7
    }

    const first_monday_time_stamp = first_january.getTime() + day_offset_to_first_monday * 24 * 3600 * 1000

    const number = Math.floor(((date.getTime() - first_monday_time_stamp) / 86400000) / 7) + 1

    return {
      number: number,
      year: date.getFullYear()
    }
  }

  /**
   * returns a timestamp to a given `date_string`
   */
  dateStringToTimestamp(date_string: string, set_hours_to_zero: boolean, use_utc_time: boolean): number {
    const date = new Date(Date.parse(date_string))
    return this.dateToTimestamp(date, set_hours_to_zero, use_utc_time)
  }

  getUTCDayTimestampOfLocalDateString(date_string: string): number {
    const date = new Date(Date.parse(date_string))
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
  }

  dateToTimestamp(date: Date, set_hours_to_zero?: boolean, use_utc_time?: boolean): number {
    if (set_hours_to_zero) {
      if (use_utc_time) {
        date.setUTCHours(0, 0, 0, 0)
      } else {
        date.setHours(0, 0, 0, 0)
      }
    }
    return date.getTime()
  }

  getCurrentTimestamp() {
    return Date.now()
  }

  dateTimeToDayMinutes(date_time: string) {
    const travel_start_date = new Date(Date.parse(date_time))
    if (typeof travel_start_date == 'undefined') {
      console.error("Can not read from Date:" + date_time)
    }
    return this.timeStringToMinutes(this.dateToTimestring(travel_start_date, false))
  }

  /**
   * takes a time string and returns the amount of minutes which have passed since 00:00
   */
  timeStringToMinutes(time: string): number {
    const splitted = time.split(':')
    return 60 * Number.parseInt(splitted[0]) + Number.parseInt(splitted[1])
  }

  formatDate(date: Date, cut_time?: boolean) {
    if (date) {
      let res = date.getFullYear() + '-' +
        ((date.getMonth() + 1).toString().padStart(2, '0')) + '-' +
        date.getDate().toString().padStart(2, '0')
      if (!cut_time) {
        res = res + 'T' +
          date.getHours().toString().padStart(2, '0') + ':' +
          date.getMinutes().toString().padStart(2, '0') + ':00'
      }
      return res
    }

    return
  }

  getTimestampsHolidays(timestamp: number, sub_state?: HolidaysState): HolidaysTypes.Holiday[] {
    const holidays = this.holidayMap.get(sub_state || 'DE').isHoliday(this.timestampToDatestring(timestamp, true))
    if (!holidays) {
      return []
    } else {
      return holidays
    }
  }

  datesAreOnSameDay(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
  }

  dateOneIsBeforeDateTwo(first: Date, second: Date): boolean {
    return first.getTime() < second.getTime();
  }

  weekDayToGermanName(week_day: WeekDay, short_name: boolean) {
    const ger_weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']

    const day_index = this.weekDays.indexOf(week_day)

    if (day_index >= 0) {
      let weekday_string = ger_weekdays[day_index]
      if (short_name) {
        weekday_string = weekday_string.slice(0, 2)
      }
      return weekday_string
    }

    return week_day
  }

  getGermanWeekDay(idx: number): string {
    return this.weekDayToGermanName(this.weekDays[idx], false)
  }
}

// Registrierung für die späte Bindung (Zyklen-Entkopplung, siehe pa-registry.ts)
PARegistry.register('timeControl', () => PATimeControl.Instance);
